/**
 * 购物车页面 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Row,
  Col,
  Table,
  Button,
  InputNumber,
  Typography,
  Card,
  Space,
  Image,
  Divider,
  Empty,
  Spin,
  Popconfirm,
  Tag,
  Grid,
  List,
} from 'antd';
import { DeleteOutlined, ShoppingOutlined, GiftOutlined } from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { useCart } from '../../contexts/CartContext';
import { discountApi } from '../../api/index';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, cartLoading, refreshCart, updateQuantity, removeItem, clearCart } = useCart();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [updatingItems, setUpdatingItems] = useState({});
  const [autoDiscount, setAutoDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);

  // 购物车商品列表
  const cartItems = cart?.items || [];

  useEffect(() => {
    refreshCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cart?.items && cart.items.length > 0) {
      const subtotal = cart.subtotal || 0;
      const itemProductIds = cart.items.map(item => item.productId);
      const quantities = cart.items.map(item => item.quantity);
      console.log('折扣请求参数:', { subtotal, itemProductIds, quantities });
      discountApi.applyAuto(subtotal, cart.items).then((discount) => {
        console.log('折扣返回:', discount);
        if (discount && discount.id) {
          setAutoDiscount(discount);
        } else {
          setAutoDiscount(null);
        }
      }).catch((err) => {
        console.error('折扣请求失败:', err);
        setAutoDiscount(null);
      });
    }
  }, [cart?.items, cart?.subtotal]);

  const handleUpdateQuantity = async (itemId, newQty) => {
    if (newQty < 1) return;
    setUpdatingItems((prev) => ({ ...prev, [itemId]: true }));
    try {
      await updateQuantity(itemId, newQty);
    } finally {
      setUpdatingItems((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRemove = async (itemId) => {
    setUpdatingItems((prev) => ({ ...prev, [itemId]: true }));
    try {
      await removeItem(itemId);
    } finally {
      setUpdatingItems((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleClearCart = async () => {
    setUpdatingItems((prev) => ({ ...prev, __clear: true }));
    try {
      await clearCart();
    } finally {
      setUpdatingItems((prev) => ({ ...prev, __clear: false }));
    }
  };

  const handleCheckout = () => {
    // 支持游客免登录下单，直接进入结账页
    navigate('/checkout');
  };

  const formatPrice = (amount) => `$${(amount || 0).toFixed(2)}`;

  const columns = [
    {
      title: '商品',
      dataIndex: 'product',
      key: 'product',
      width: isMobile ? '50%' : '40%',
      render: (_, record) => (
        <Space size={12}>
          <Image
            src={record.imageUrl}
            alt={record.productTitle}
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            preview={false}
          />
          <div style={{ maxWidth: isMobile ? 120 : 200 }}>
            <Text
              strong
              ellipsis={{ rows: 2 }}
              style={{ color: COLORS.ink, lineHeight: 1.4 }}
            >
              {record.productTitle}
            </Text>
            {record.variantTitle && (
              <div>
                <Tag
                  size="small"
                  style={{
                    background: `${COLORS.bambooGreen}15`,
                    borderColor: COLORS.bambooGreen,
                    color: COLORS.bambooGreen,
                    fontSize: 11,
                  }}
                >
                  {record.variantTitle}
                </Tag>
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: '单价',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) => (
        <Text style={{ color: COLORS.ink, fontWeight: 500, textAlign: 'right', display: 'block' }}>
          {formatPrice(price)}
        </Text>
      ),
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (_, record) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(val) => handleUpdateQuantity(record.id, val)}
          loading={updatingItems[record.id]}
          size="small"
          style={{ borderRadius: 6, borderColor: COLORS.lightBorder }}
        />
      ),
    },
    {
      title: '小计',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 100,
      render: (_, record) => (
        <Text
          strong
          style={{ color: COLORS.cinnabar, fontSize: 15, textAlign: 'right', display: 'block' }}
        >
          {formatPrice(record.price * record.quantity)}
        </Text>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Popconfirm
          title="确认删除？"
          onConfirm={() => handleRemove(record.id)}
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={updatingItems[record.id]}
          />
        </Popconfirm>
      ),
    },
  ];

  // 计算折扣金额
  const subtotal = cart?.subtotal || 0;
  let discountAmount = 0;
  if (autoDiscount) {
    if (autoDiscount.type === 'percentage') {
      // 百分比折扣：subtotal * value / 100
      discountAmount = subtotal * (autoDiscount.value || 0) / 100;
    } else if (autoDiscount.type === 'fixed_amount') {
      // 固定金额折扣
      discountAmount = Math.min(autoDiscount.value || 0, subtotal);
    } else if (autoDiscount.type === 'free_shipping') {
      // 免运费折扣（暂不处理）
      discountAmount = 0;
    }
  }
  const total = Math.max(0, subtotal - discountAmount);

  // 检查购物车商品库存是否不足
  const lowStockItems = cartItems.filter(item =>
    item.inventoryQuantity != null && item.inventoryQuantity < item.quantity
  );
  const hasLowStock = lowStockItems.length > 0;

  if (cartLoading) {
    return (
      <StorefrontLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <Spin size="large" />
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', overflowX: 'hidden' }}>
        <Title
          level={2}
          style={{
            color: COLORS.ink,
            marginBottom: 24,
            fontSize: 24,
            letterSpacing: '0.15em',
          }}
        >
          购物车
        </Title>

        {!cart?.items?.length ? (
          <Card
            style={{
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            }}
          >
            <Empty
              description={
                <Text style={{ color: COLORS.lightInk }}>购物车空空如也</Text>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                onClick={() => navigate('/products')}
                style={{
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                去逛逛
              </Button>
            </Empty>
          </Card>
        ) : (
          <Row gutter={[32, 32]}>
            <Col xs={24} lg={16}>
              <Card
                style={{
                  borderRadius: 12,
                  border: 'none',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
                bodyStyle={{ padding: isMobile ? 16 : 20 }}
              >
                <Row justify="end" style={{ marginBottom: 12 }}>
                  <Popconfirm
                    title="确认清空购物车？"
                    onConfirm={handleClearCart}
                    okText="清空"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={updatingItems['__clear']}
                      style={{ color: COLORS.lightInk }}
                    >
                      清空购物车
                    </Button>
                  </Popconfirm>
                </Row>
                {isMobile ? (
                  <List
                    dataSource={cart.items}
                    rowKey="id"
                    renderItem={(record) => (
                      <List.Item
                        style={{
                          borderBottom: `1px solid ${COLORS.lightBorder}`,
                          padding: '16px 0',
                        }}
                      >
                        <Row gutter={12} style={{ width: '100%' }}>
                          <Col span={5}>
                            <Image
                              src={record.imageUrl}
                              alt={record.productTitle}
                              width={56}
                              height={56}
                              style={{ objectFit: 'cover', borderRadius: 8 }}
                              preview={false}
                            />
                          </Col>
                          <Col span={19}>
                            <Text
                              strong
                              style={{
                                color: COLORS.ink,
                                fontSize: 14,
                                display: 'block',
                                marginBottom: 4,
                              }}
                            >
                              {record.productTitle}
                            </Text>
                            {record.variantTitle && (
                              <Tag
                                size="small"
                                style={{
                                  background: `${COLORS.bambooGreen}15`,
                                  borderColor: COLORS.bambooGreen,
                                  color: COLORS.bambooGreen,
                                  fontSize: 11,
                                  marginBottom: 8,
                                }}
                              >
                                {record.variantTitle}
                              </Tag>
                            )}
                            <Row justify="space-between" align="middle">
                              <Col>
                                <Text
                                  style={{ color: COLORS.lightInk, fontSize: 13 }}
                                >
                                  {formatPrice(record.price)}
                                </Text>
                              </Col>
                              <Col>
                                <Space>
                                  <InputNumber
                                    min={1}
                                    value={record.quantity}
                                    onChange={(val) =>
                                      handleUpdateQuantity(record.id, val)
                                    }
                                    loading={updatingItems[record.id]}
                                    size="small"
                                    style={{
                                      width: 70,
                                      borderRadius: 6,
                                      borderColor: COLORS.lightBorder,
                                    }}
                                  />
                                  <Popconfirm
                                    title="确认删除？"
                                    onConfirm={() => handleRemove(record.id)}
                                    okText="删除"
                                    cancelText="取消"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      loading={updatingItems[record.id]}
                                    />
                                  </Popconfirm>
                                </Space>
                              </Col>
                            </Row>
                            <Row justify="space-between" style={{ marginTop: 8 }}>
                              <Text style={{ color: COLORS.lightInk, fontSize: 12 }}>
                                小计
                              </Text>
                              <Text
                                strong
                                style={{ color: COLORS.cinnabar, fontSize: 15 }}
                              >
                                {formatPrice(record.price * record.quantity)}
                              </Text>
                            </Row>
                          </Col>
                        </Row>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={cart.items}
                    rowKey="id"
                    pagination={false}
                    showHeader
                    scroll={{ x: 'max-content' }}
                    rowSelection={null}
                    locale={{ emptyText: <Empty.PRESENTED_IMAGE_SIMPLE /> }}
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                style={{
                  borderRadius: 12,
                  border: 'none',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  position: isMobile ? 'static' : 'sticky',
                  top: 96,
                }}
              >
                <Title level={4} style={{ color: COLORS.ink, marginBottom: 16 }}>
                  订单摘要
                </Title>

                {/* 商品列表 */}
                <div style={{ marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
                  {cartItems.map((item) => (
                    <Row
                      key={item.id}
                      justify="space-between"
                      align="middle"
                      style={{
                        marginBottom: 12,
                        paddingBottom: 12,
                        borderBottom: `1px solid ${COLORS.lightBorder}`,
                      }}
                    >
                      <Space size={8}>
                        <Image
                          src={item.imageUrl}
                          alt={item.productTitle}
                          width={40}
                          height={40}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                          preview={false}
                        />
                        <div>
                          <Text
                            strong
                            ellipsis={{ rows: 1 }}
                            style={{
                              display: 'block',
                              maxWidth: 150,
                              color: COLORS.ink,
                              fontSize: 13,
                            }}
                          >
                            {item.productTitle}
                          </Text>
                          {item.variantTitle && (
                            <Text
                              style={{
                                color: COLORS.lightInk,
                                fontSize: 11,
                              }}
                            >
                              {item.variantTitle}
                            </Text>
                          )}
                          <Text
                            style={{
                              color: COLORS.lightInk,
                              fontSize: 11,
                            }}
                          >
                            ×{item.quantity}
                          </Text>
                        </div>
                      </Space>
                      <Text
                        strong
                        style={{ color: COLORS.cinnabar, fontSize: 13 }}
                      >
                        {formatPrice(item.price * item.quantity)}
                      </Text>
                    </Row>
                  ))}
                </div>

                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <Row justify="space-between">
                    <Text style={{ color: COLORS.lightInk }}>商品小计</Text>
                    <Text strong style={{ color: COLORS.ink }}>
                      {formatPrice(subtotal)}
                    </Text>
                  </Row>

                  {autoDiscount && (
                    <Row justify="space-between" align="middle">
                      <Space>
                        <Tag
                          icon={<GiftOutlined />}
                          style={{
                            background: `${COLORS.cinnabar}15`,
                            borderColor: COLORS.cinnabar,
                            color: COLORS.cinnabar,
                          }}
                        >
                          优惠
                        </Tag>
                        <Text style={{ color: COLORS.lightInk }}>
                          {autoDiscount.title || '自动折扣'}
                        </Text>
                      </Space>
                      <Text type="success" style={{ color: COLORS.cinnabar }}>
                        -{formatPrice(discountAmount)}
                      </Text>
                    </Row>
                  )}

                  <Divider style={{ borderColor: COLORS.lightBorder, margin: '12px 0' }} />

                  <Row justify="space-between" align="middle">
                    <Title level={5} style={{ margin: 0, color: COLORS.ink }}>
                      合计
                    </Title>
                    <Text
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: COLORS.cinnabar,
                      }}
                    >
                      {formatPrice(total)}
                    </Text>
                  </Row>

                  <Popconfirm
                    title="确认清空购物车？"
                    onConfirm={handleClearCart}
                    okText="清空"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      block
                      size="large"
                      icon={<DeleteOutlined />}
                      loading={updatingItems['__clear']}
                      style={{
                        marginTop: 16,
                        height: 44,
                        borderRadius: 8,
                        fontSize: 14,
                        color: COLORS.lightInk,
                        borderColor: COLORS.lightBorder,
                      }}
                    >
                      清空购物车
                    </Button>
                  </Popconfirm>

                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={handleCheckout}
                    icon={<ShoppingOutlined />}
                    disabled={hasLowStock}
                    style={{
                      marginTop: 12,
                      height: 48,
                      borderRadius: 8,
                      fontSize: 16,
                      background: hasLowStock ? COLORS.lightBorder : COLORS.teaBrown,
                      borderColor: hasLowStock ? COLORS.lightBorder : COLORS.teaBrown,
                    }}
                  >
                    {hasLowStock ? '部分商品库存不足' : '去结算'}
                  </Button>

                  {hasLowStock && (
                    <Text
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        color: COLORS.cinnabar,
                        fontSize: 12,
                        marginTop: 8,
                      }}
                    >
                      {lowStockItems.map(item => item.productTitle).join('、')} 库存不足
                    </Text>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </StorefrontLayout>
  );
}
